import { supabase } from '@/integrations/supabase/client';

export type WriterCompileJobStatus =
  | 'queued'
  | 'running'
  | 'success'
  | 'failed'
  | 'cancelled';

export interface EnqueueCompileInput {
  versionId: string;
}

export interface EnqueueCompileResult {
  jobId: string;
  status: WriterCompileJobStatus;
}

/**
 * Request an explicit re-run of the compile pipeline for a given
 * writer_versions row. New inserts are auto-enqueued by a DB trigger; this
 * helper is only for retries and manual kicks from the version scrubber.
 */
export async function enqueueWriterCompile(
  input: EnqueueCompileInput,
): Promise<EnqueueCompileResult> {
  if (!input.versionId) throw new Error('versionId required');

  const { data, error } = await supabase.functions.invoke<{
    job_id: string;
    status: WriterCompileJobStatus;
    error?: string;
  }>('writer-compile/enqueue', {
    body: { version_id: input.versionId },
  });
  if (error) throw new Error(error.message);
  if (!data?.job_id) throw new Error(data?.error ?? 'enqueue failed');
  return { jobId: data.job_id, status: data.status };
}

export interface ProcessCompileResult {
  processed: Array<{
    id: string;
    status: WriterCompileJobStatus | 'failed';
    error?: string;
    note?: string;
  }>;
}

/**
 * Drain a small batch of queued compile jobs owned by the caller. Used by
 * the admin dev harness while the GCP compile worker isn't wired up.
 */
export async function processWriterCompileQueue(
  limit = 3,
): Promise<ProcessCompileResult> {
  const { data, error } = await supabase.functions.invoke<ProcessCompileResult>(
    'writer-compile/process',
    { body: { limit } },
  );
  if (error) throw new Error(error.message);
  return data ?? { processed: [] };
}
